import { useEffect, useRef } from 'react';
import { CreditGiftCardMetadata } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useTranslation } from '../../lib/i18n';
import { fireConfetti } from './confetti';
import { Typography } from './ui';
import styles from './MilestoneCard.module.css';

/**
 * The in-chat "the agent gave you credits" moment — rendered in place of a normal assistant bubble for
 * a row carrying `metadata.creditGiftCard` (see `readCreditGiftCard` in common-lib).
 *
 * Reuses the milestone card's shell and its `fireConfetti` burst verbatim rather than growing a second
 * celebratory visual language: there is one success surface in this product and one burst, kept 1:1
 * with iOS/Android.
 *
 * **The two kinds differ in exactly one thing, and it is not decoration.** `appreciation` fires the
 * confetti; `reimbursement` does not. Throwing confetti over an apology celebrates the user's bad
 * afternoon, which is a worse message than sending nothing at all — so the make-good gets the same
 * quiet card with the same amount and no party.
 *
 * The amount comes from the marker and nothing else. It is what the SERVER actually granted, which may
 * be less than the agent asked for, and a card congratulating someone on credits they did not receive
 * is the one failure this feature must never produce.
 */

type Props = { card: CreditGiftCardMetadata };

const CHECK_PATH = 'M6.67 12 L10 15.33 L17.33 8';

export function CreditGiftCard({ card }: Props) {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const haloRef = useRef<SVGCircleElement>(null);
  const discRef = useRef<SVGCircleElement>(null);
  const ringRef = useRef<SVGCircleElement>(null);
  const checkRef = useRef<SVGPathElement>(null);
  const celebrates = card.kind === 'appreciation';

  useEffect(() => {
    const el = cardRef.current;
    const canvas = canvasRef.current;
    const halo = haloRef.current;
    const disc = discRef.current;
    const ring = ringRef.current;
    const check = checkRef.current;
    const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !el || typeof el.animate !== 'function') {
      return;
    }
    // Same entrance reel and timings as MilestoneCard (ring 0–420, disc 340–580, halo 420–1020,
    // check 480–820), so the two cards settle identically in the same thread.
    el.animate(
      [
        { opacity: 0, transform: 'scale(0.96) translateY(8px)' },
        { opacity: 1, transform: 'scale(1) translateY(0)' },
      ],
      { duration: 420, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'both' },
    );
    ring?.animate([{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], { duration: 420, easing: 'ease-in-out', fill: 'both' });
    ring?.animate([{ opacity: 1, offset: 0 }, { opacity: 1, offset: 0.59 }, { opacity: 0, offset: 1 }], { duration: 580, fill: 'both' });
    disc?.animate([{ opacity: 0, transform: 'scale(0.6)' }, { opacity: 1, transform: 'scale(1)' }], {
      duration: 240,
      delay: 340,
      easing: 'ease-out',
      fill: 'both',
    });
    halo?.animate([{ opacity: 0.3, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(1.8)' }], {
      duration: 600,
      delay: 420,
      easing: 'ease-out',
      fill: 'both',
    });
    check?.animate([{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], {
      duration: 340,
      delay: 480,
      easing: 'ease-in-out',
      fill: 'both',
    });
    if (!celebrates) {
      return;
    }
    const confettiTimer = window.setTimeout(() => {
      if (canvas && disc) {
        fireConfetti(canvas, disc.ownerSVGElement ?? disc);
      }
    }, 820);
    return () => window.clearTimeout(confettiTimer);
  }, [celebrates]);

  // Localized here, never composed by the agent: the header is a number in a sentence, and its word
  // order and pluralization belong to the locale, not to whatever language the model happened to
  // reason in. The `note` beside it IS the agent's own writing, already in the user's language.
  const header = t('creditGift.header').replace('{credits}', String(card.credits));
  const body = card.note ?? t(`creditGift.${card.kind}.body`);

  return (
    <div ref={cardRef} className={styles.bubble}>
      {celebrates && <canvas ref={canvasRef} aria-hidden className={styles.confetti} />}
      <div className={styles.header}>
        <svg className={styles.badge} width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
          <circle ref={haloRef} className={styles.halo} cx="12" cy="12" r="12" />
          <circle ref={discRef} className={styles.disc} cx="12" cy="12" r="12" />
          <circle ref={ringRef} className={styles.ring} cx="12" cy="12" r="11" pathLength={1} />
          <path ref={checkRef} className={styles.check} d={CHECK_PATH} pathLength={1} />
        </svg>
        <div className={styles.text}>
          <Typography variant="h3" component="h3" color="strong" className={styles.title}>
            {header}
          </Typography>
          {body && (
            <Typography variant="p2" color="strong" className={styles.body}>
              {body}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
}
